/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentType } from 'react';

import { jsx } from '@compiled/react';

import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import EditIcon from '@atlaskit/icon/core/edit';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationBlockIcon from '@atlaskit/icon/core/quotation-block';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import TextIcon from '@atlaskit/icon/core/text';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common/constants';
import type { ManifestEditorToolbarActions } from '@atlaskit/native-embeds-common/types';
import { createEditorToolbarActions } from '@atlaskit/native-embeds-common/utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import type { NativeEmbedChildCommand } from '@atlassian/native-embeds-core/types/commands/types';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
} from '@atlassian/native-embeds-core/types/experience/types';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core/types/features/main';
import type { NativeEmbedFeature } from '@atlassian/native-embeds-core/types/features/main';
import type {
	LoadingComponentProps,
	ResolvedExperienceMeta,
} from '@atlassian/native-embeds-core/types/ui-config';

export const PAGE_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('page');

const pageIcons = [PageLiveDocIcon, EditIcon, TextIcon, QuotationBlockIcon];

// Match page URLs: /wiki/spaces/<key>/pages/<id>
// Match blog URLs: /wiki/spaces/<key>/blog/<id> or /wiki/spaces/<key>/blog/<year>/<month>/<day>/<id>
const pagePathPattern = /\/wiki\/spaces\/[^/]+\/(pages\/\d+|blog\/(\d+\/){0,3}\d+)/;
const pageShortLinkUrlPattern = /^https:\/\/[^/?#]+\/wiki\/x\/[A-Za-z0-9_-]+\/?(?:[?#].*)?$/;

const isPageUrl = (url: string): boolean =>
	pagePathPattern.test(url) || pageShortLinkUrlPattern.test(url);

const isEditUrl = (url: string): boolean =>
	/\/wiki\/spaces\/[^/]+\/(pages|blog)\/(edit-embed|edit-v2)\/\d+/.test(url);

// PGXT-10098: keep blog icons correct before resolved metadata is available.
const blogPathPattern = /\/wiki\/spaces\/[^/]+\/blog\//;
const isBlogUrl = (url: string): boolean => {
	try {
		return blogPathPattern.test(new URL(url).pathname);
	} catch {
		return blogPathPattern.test(url);
	}
};

const resolveTitleIcon = (
	meta: ResolvedExperienceMeta,
	url: string,
): ComponentType<NewCoreIconProps> => {
	if (meta.documentType === 'schema:DigitalDocument') {
		return PageLiveDocIcon;
	}
	if (meta.documentType === 'schema:BlogPosting' || isBlogUrl(url)) {
		return QuotationMarkIcon;
	}
	return PageIcon;
};

/**
 * Experience manifest for a page embed.
 * Register with getNativeEmbedFacade().registerExperienceManifest(PageExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const PageExperienceManifest: {
	readonly enforceQueryParams: true;
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly shouldFocusOnClick: (url: string, meta: ResolvedExperienceMeta) => boolean;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, _isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: Record<string, never>;
		readonly headerControls: {
			readonly editButton: {
				readonly onSwitchToEditing: (currentUrl: string) => string;
				readonly onSwitchToViewing: (currentUrl: string) => string;
				readonly show: (meta: ResolvedExperienceMeta) => boolean;
			};
		};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly resolveTitleIcon: (
			meta: ResolvedExperienceMeta,
			url: string,
		) => ComponentType<NewCoreIconProps>;
		readonly showHeader: (_meta: ResolvedExperienceMeta, url: string) => boolean;
		readonly titleIcon: ComponentType<NewCoreIconProps>;
	};
} = {
	enforceQueryParams: true,
	id: PAGE_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {},
	isUrlMatch: isPageUrl,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	isActive: (): true => true,
	shouldFocusOnClick: (url: string, meta: ResolvedExperienceMeta): boolean => {
		// Don't steal focus for live docs (documentType === 'schema:DigitalDocument')
		if (meta.documentType === 'schema:DigitalDocument') {
			return false;
		}
		// Don't steal focus when the page is in edit mode
		if (isEditUrl(url)) {
			return false;
		}
		return true;
	},
	transformUrl: (url: string, _isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		urlObj.searchParams.set('sample-param', 'true');
		return urlObj.toString();
	},
	uiConfig: {
		showHeader: (_meta: ResolvedExperienceMeta, url: string): boolean => !isEditUrl(url),
		headerControls: {
			editButton: {
				show: (meta: ResolvedExperienceMeta): boolean =>
					meta.documentType !== 'schema:DigitalDocument', // Don't show edit button for live docs
				onSwitchToEditing: (currentUrl: string, originalUrl?: string): string => {
					const url = new URL(currentUrl);
					// Detect whether this is originally a blog URL. The iframe's internal
					// SPA router may resolve blog URLs (e.g. /blog/2026/04/29/<id>) to
					// their canonical /pages/<id> form, so iframeSrc (currentUrl) may
					// show /pages/<id> even for blogs. We check the originalUrl (the URL
					// before iframe navigation) to detect blogs reliably; fall back to
					// the current URL when no originalUrl is provided.
					const contentIsBlog = isBlogUrl(originalUrl ?? currentUrl);

					if (contentIsBlog) {
						// Blogs use /blog/edit-v2/<id> because there is no /blog/edit-embed/ route,
						// which would cause a redirect that strips native embed query params.
						// Extract the content ID from either the blog URL or the resolved /pages/<id> URL.
						const blogMatch = url.pathname.match(/\/blog\/(?:\d+\/\d+\/\d+\/)?(\d+)(?:\/[^/]*)?$/);
						const pagesMatch = url.pathname.match(/\/pages\/(\d+)/);
						const contentId = blogMatch?.[1] ?? pagesMatch?.[1];
						if (contentId) {
							// Replace the path portion after /spaces/<key>/ with /blog/edit-v2/<id>
							url.pathname = url.pathname.replace(
								/\/spaces\/([^/]+)\/.*$/,
								`/spaces/$1/blog/edit-v2/${contentId}`,
							);
						}
					} else {
						// Pages use /pages/edit-embed/<id> (dedicated embedded editor route).
						url.pathname = url.pathname.replace(/\/pages\/(\d+)/, '/pages/edit-embed/$1');
					}
					url.searchParams.set('initialAllowedFeatures', 'delete-draft');
					return url.toString();
				},
				onSwitchToViewing: (currentUrl: string, _originalUrl?: string): string => {
					const url = new URL(currentUrl);
					// Reverse the edit URL transforms back to view URLs
					url.pathname = url.pathname
						.replace(/\/pages\/edit-embed\/(\d+)/, '/pages/$1')
						.replace(/\/blog\/edit-v2\/(\d+)/, '/blog/$1');
					url.searchParams.delete('initialAllowedFeatures');
					return url.toString();
				},
			},
		},
		editorToolbarActions: () =>
			createEditorToolbarActions({
				items: [
					BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
					BUILTIN_TOOLBAR_KEYS.SUMMARISE,
					...(fg('maui_remixable_page_native_embeds') ? [BUILTIN_TOOLBAR_KEYS.REMIX] : []),
					BUILTIN_TOOLBAR_KEYS.SEPARATOR,
					BUILTIN_TOOLBAR_KEYS.EMBED,
					BUILTIN_TOOLBAR_KEYS.EDIT,
					...(fg('cc-maui-toolbar-separators-update-jira') ? [] : [BUILTIN_TOOLBAR_KEYS.SEPARATOR]),
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
					...(fg('cc-maui-toolbar-separators-update-jira')
						? []
						: [
								BUILTIN_TOOLBAR_KEYS.SEPARATOR,
								BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
								BUILTIN_TOOLBAR_KEYS.SEPARATOR,
							]),
				],
				moreItems: [
					BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
					BUILTIN_TOOLBAR_KEYS.COPY,
					BUILTIN_TOOLBAR_KEYS.DELETE,
				],
			}),
		editorToolbarHandlers: {},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={pageIcons}
				testId={props.testId}
			/>
		),
		resolveTitleIcon,
		titleIcon: PageIcon,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
